import * as momentTz from 'moment-timezone';
import { RepeatUnit, TIME_ZONES } from '../constants/constants';

const DAY_LABELS: Record<number, string> = {
  0: 'Sun',
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
};

const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

export function addMinutesToTime(time: string, minutes: number): string {
  const [h, m] = time.split(':').map((part) => parseInt(part, 10));
  const total = ((h * 60 + m + minutes) % (24 * 60) + 24 * 60) % (24 * 60);
  const outH = Math.floor(total / 60);
  const outM = total % 60;
  return `${String(outH).padStart(2, '0')}:${String(outM).padStart(2, '0')}`;
}

export function formatDuration(startTime?: string, endTime?: string): string | null {
  if (!startTime || !endTime) {
    return null;
  }
  const [sh, sm] = startTime.split(':').map((part) => parseInt(part, 10));
  const [eh, em] = endTime.split(':').map((part) => parseInt(part, 10));
  let minutes = (eh * 60 + em) - (sh * 60 + sm);
  // Classes that run past midnight end on the next day
  if (minutes < 0) {
    minutes += 24 * 60;
  }
  if (minutes === 0) {
    return null;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours && mins) {
    return `${hours}h ${mins}m`;
  }
  if (hours) {
    return `${hours}h`;
  }
  return `${mins}m`;
}

export function describeRecurrenceDays(days: number[]): string {
  if (!days?.length) {
    return '';
  }
  const ordered = WEEK_ORDER.filter((d) => days.includes(d));
  return ordered.map((d) => DAY_LABELS[d]).join(', ');
}

export function describeRecurrence(
  days: number[],
  repeatCount: number,
  repeatUnit: RepeatUnit,
  untilDate?: string,
): string {
  if (!days?.length || !repeatCount) {
    return 'Does not repeat';
  }
  const dayList = describeRecurrenceDays(days);
  const untilLabel = untilDate ? formatDateLabel(untilDate) : '';
  return `Repeats weekly on ${dayList} — ${repeatCount} ${repeatUnit}${untilLabel ? `, until ${untilLabel}` : ''}`;
}

export function formatDateLabel(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function isPastDateTime(dateStr: string, timeStr?: string): boolean {
  if (!dateStr) {
    return false;
  }
  const now = new Date();
  const candidate = timeStr ? new Date(`${dateStr}T${timeStr}`) : new Date(`${dateStr}T23:59:59`);
  return candidate.getTime() < now.getTime();
}

export function formatTime12Hour(time: string): string {
  const [h, m] = time.split(':').map((part) => parseInt(part, 10));
  const period = h < 12 ? 'AM' : 'PM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

export function parseTime12Hour(time: string): string {
  const match = time.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) {
    return time;
  }
  const [, hourStr, minuteStr, period] = match;
  let hour = parseInt(hourStr, 10) % 12;
  if (period.toUpperCase() === 'PM') {
    hour += 12;
  }
  return `${String(hour).padStart(2, '0')}:${minuteStr}`;
}

export function isLikelyUrl(value: string): boolean {
  return /^https?:\/\//i.test(value.trim());
}

// A class link as an http(s) URL; bare domains like "meet.google.com/abc"
// get https://, and anything else (e.g. "Room 4B") isn't a link
export function toLinkHref(value: string | undefined | null): string | null {
  const trimmed = (value ?? '').trim();
  if (isLikelyUrl(trimmed)) {
    return trimmed;
  }
  return /^[\w-]+(\.[\w-]+)+(:\d+)?(\/\S*)?$/.test(trimmed)
    ? `https://${trimmed}`
    : null;
}

export function addDaysUtc(dateStr: string, days: number): string {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function getUtcDay(dateStr: string): number {
  return new Date(`${dateStr}T00:00:00Z`).getUTCDay();
}

export function generateWeeklyOccurrences(
  startDate: string,
  untilDate: string,
  days: number[],
  maxOccurrences = 366,
  intervalWeeks = 1,
): string[] {
  if (!days?.length || untilDate < startDate) {
    return [];
  }
  const interval = intervalWeeks > 0 ? intervalWeeks : 1;
  const dates: string[] = [];
  let cursor = startDate;
  let weekIndex = 0;
  while (cursor <= untilDate && dates.length < maxOccurrences) {
    if (weekIndex % interval === 0 && days.includes(getUtcDay(cursor))) {
      dates.push(cursor);
    }
    cursor = addDaysUtc(cursor, 1);
    if (getUtcDay(cursor) === WEEK_ORDER[0]) {
      weekIndex++;
    }
  }
  return dates;
}

// The viewer's own time zone, e.g. "Asia/Calcutta"
export function viewerTimeZone(): string {
  return momentTz.tz.guess();
}

// Short name of the viewer's time zone at a given moment (default now),
// e.g. "IST", or "EDT" / "EST" depending on daylight saving
export function viewerTimeZoneAbbr(at?: Date | momentTz.Moment): string {
  return momentTz.tz(at ?? new Date(), viewerTimeZone()).zoneAbbr();
}

// A class's date (YYYY-MM-DD) and time (HH:mm) in its batch time zone
// (TIME_ZONES id), as the same instant in the viewer's time zone
export function toViewerTime(
  date: string,
  time: string,
  timeZoneId?: number | null
): momentTz.Moment {
  const viewerZone = viewerTimeZone();
  const zone = (timeZoneId && TIME_ZONES[timeZoneId - 1]) || viewerZone;
  return momentTz
    .tz(`${date} ${time}`, 'YYYY-MM-DD HH:mm', zone)
    .tz(viewerZone);
}

// The Monday–Sunday week (YYYY-MM-DD) holding a date. The event API only
// moves a class within this week
export function classWeekOf(date: string): { start: string; end: string } {
  const day = momentTz.utc(date, 'YYYY-MM-DD');
  return {
    start: day.clone().startOf('isoWeek').format('YYYY-MM-DD'),
    end: day.clone().endOf('isoWeek').format('YYYY-MM-DD')
  };
}

// e.g. "Mon, Nov 9 – Sun, Nov 15"
export function formatClassWeek(date: string): string {
  const { start, end } = classWeekOf(date);
  const label = (d: string) => momentTz.utc(d, 'YYYY-MM-DD').format('ddd, MMM D');
  return `${label(start)} – ${label(end)}`;
}
